import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Search, X, RefreshCw, ArrowUpRight, ChevronDown } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { InvitationPreview } from '../components/InvitationPreview';
import { PartnerTag, PartnerBanner, ALL_PARTNERS } from '../components/PartnerTag';
import { Skeleton } from '../components/ui/skeleton';
import { API_URL } from '../utils/api';
import { loadFontStylesheets } from '../utils/fonts';
import { formatPrice, getTemplatePrice } from '../utils/format';
import { getErrorMessage } from '../utils/errors';
import { useDocumentTitle } from '../hooks/use-document-title';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-paper';

const chipBase = `inline-flex items-center h-10 sm:h-11 px-4 sm:px-5 rounded-full text-sm sm:text-[15px] font-semibold whitespace-nowrap transition-colors ${focusRing}`;
const chipActive = 'bg-ink text-paper border border-ink';
const chipInactive = 'gradient-border text-ink hover:text-[#6E5B1C]';

const primaryPill = `inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-ink text-paper text-sm font-semibold hover:bg-ink/90 transition-colors ${focusRing}`;
const secondaryPill = `inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full gradient-border gb-cream text-ink text-sm font-semibold hover:text-[#6E5B1C] transition-colors ${focusRing}`;

// '' = the gallery's usual order (by design code)
const SORT_OPTIONS = [
  { value: '', label: 'Featured' },
  { value: 'newest', label: 'Newest' },
  { value: 'price-asc', label: 'Price: low to high' },
  { value: 'price-desc', label: 'Price: high to low' },
  { value: 'popular', label: 'Most popular' },
];

// brand filter value for Azamein's own designs (partners use their id)
const AZAMEIN_BRAND = 'azamein';

const TemplateCardSkeleton = () => (
  <div className="flex flex-col gap-3 sm:gap-4" aria-hidden="true">
    <div className="rounded-2xl bg-cream p-3 sm:p-6 lg:p-7">
      <Skeleton className="aspect-[5/7] rounded-[4px] bg-ink/10" />
    </div>
    <div className="px-1 space-y-2">
      <Skeleton className="h-4 w-3/4 bg-ink/10" />
      <Skeleton className="h-3 w-1/3 bg-ink/10" />
    </div>
  </div>
);

const StatePanel = ({ children, ...props }) => (
  <div
    className="gradient-border gb-cream rounded-2xl text-center px-6 py-14 sm:py-20 max-w-2xl mx-auto"
    {...props}
  >
    {children}
  </div>
);

export default function TemplatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [popularOrder, setPopularOrder] = useState([]);
  const [brands, setBrands] = useState([]);

  const selectedCategory = searchParams.get('category') || '';
  const requestedSort = searchParams.get('sort') || '';
  // "Most popular" only shows once there are sales to rank by
  const sortOptions = SORT_OPTIONS.filter((o) => o.value !== 'popular' || popularOrder.length > 0);
  const sort = sortOptions.some((o) => o.value === requestedSort) ? requestedSort : '';
  const brandById = useMemo(() => new Map(brands.map((b) => [b.id, b])), [brands]);
  const requestedBrand = searchParams.get('brand') || '';
  // an unknown brand (e.g. an old link to a partner no longer listed) shows everything
  const selectedBrand =
    requestedBrand === AZAMEIN_BRAND || requestedBrand === ALL_PARTNERS || brandById.has(requestedBrand)
      ? requestedBrand
      : '';
  const selectedPartner = brandById.get(selectedBrand) || null;
  const selectedCategoryName = categories.find((c) => c.id === selectedCategory)?.name;
  useDocumentTitle(selectedCategoryName ? `${selectedCategoryName} templates` : 'Invitation templates');

  useEffect(() => {
    axios
      .get(`${API_URL}/categories`)
      .then((response) => setCategories(Array.isArray(response.data) ? response.data : []))
      .catch(() => setCategories([]));

    axios
      .get(`${API_URL}/fonts`)
      .then((response) => loadFontStylesheets(response.data))
      .catch(() => {
        // Previews fall back to default fonts.
      });

    axios
      .get(`${API_URL}/templates/popular`)
      .then((response) => setPopularOrder(Array.isArray(response.data?.order) ? response.data.order : []))
      .catch(() => setPopularOrder([]));

    axios
      .get(`${API_URL}/templates/brands`)
      .then((response) => setBrands(Array.isArray(response.data?.brands) ? response.data.brands : []))
      .catch(() => setBrands([]));
  }, []);

  // partner templates carry their own fonts (left out of the site-wide /fonts list)
  useEffect(() => {
    templates.forEach((t) => loadFontStylesheets(t.fonts));
  }, [templates]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const url = selectedCategory
      ? `${API_URL}/templates?category_id=${encodeURIComponent(selectedCategory)}`
      : `${API_URL}/templates`;

    axios
      .get(url)
      .then((response) => {
        if (!cancelled) setTemplates(Array.isArray(response.data) ? response.data : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setTemplates([]);
          setError(getErrorMessage(err, 'We could not load templates right now.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCategory, reloadKey]);

  // keep the other URL param when one changes
  const setParam = useCallback(
    (key, value) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      });
    },
    [setSearchParams]
  );
  const handleCategoryChange = useCallback((categoryId) => setParam('category', categoryId), [setParam]);
  const handleSortChange = useCallback((value) => setParam('sort', value), [setParam]);
  const handleBrandChange = useCallback((value) => setParam('brand', value), [setParam]);
  // A partner tag opens that partner's full selection: start from the top, without a leftover search
  const handlePartnerOpen = useCallback(() => {
    setSearchQuery('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const query = searchQuery.trim().toLowerCase();
  const filteredTemplates = useMemo(() => {
    const matches = templates.filter(
      (template) =>
        (!selectedBrand ||
          (selectedBrand === AZAMEIN_BRAND
            ? !template.partner_id
            : selectedBrand === ALL_PARTNERS
              ? Boolean(template.partner_id)
              : template.partner_id === selectedBrand)) &&
        ((template.name || '').toLowerCase().includes(query) ||
        String(template.template_code || '').toLowerCase().includes(query) ||
        // 3-digit background numbers are for the website only (the phone line uses the 4-digit code)
        String(template.background_code || '').toLowerCase().includes(query) ||
        (brandById.get(template.partner_id)?.name || '').toLowerCase().includes(query))
    );
    // Array.prototype.sort is stable, so ties keep the usual (design code) order
    if (sort === 'newest') {
      const time = (t) => Date.parse(t.created_at) || 0;
      return [...matches].sort((a, b) => time(b) - time(a));
    }
    if (sort === 'price-asc' || sort === 'price-desc') {
      const dir = sort === 'price-asc' ? 1 : -1;
      return [...matches].sort((a, b) => dir * ((getTemplatePrice(a) || 0) - (getTemplatePrice(b) || 0)));
    }
    if (sort === 'popular') {
      const rank = new Map(popularOrder.map((id, i) => [id, i]));
      const r = (t) => (rank.has(t.id) ? rank.get(t.id) : Infinity);
      return [...matches].sort((a, b) => (r(a) === r(b) ? 0 : r(a) < r(b) ? -1 : 1));
    }
    return matches;
  }, [templates, query, sort, popularOrder, selectedBrand, brandById]);

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Navbar />

      <main id="main-content" className="flex-1 pt-28 sm:pt-32 pb-20 sm:pb-28 px-4 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-10 mb-7 sm:mb-8"
          >
            <div className="flex flex-col gap-3 min-w-0">
              <p className="text-xs sm:text-[13px] font-bold uppercase tracking-[0.16em] text-[#6E5B1C]">
                Design gallery
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] font-semibold tracking-[-0.015em] text-ink break-words">
                {selectedCategoryName ? (
                  <>
                    {selectedCategoryName} <span className="italic font-medium">invitations</span>
                  </>
                ) : (
                  <>
                    All <span className="italic font-medium">designs</span>
                  </>
                )}
              </h1>
              <p className="text-base sm:text-[17px] text-[#4A4640] max-w-xl">
                Every design can be personalized in Hebrew, Yiddish or English. Free preview before you pay.
              </p>
            </div>

            {/* Search */}
            <div className="relative w-full lg:w-[320px] shrink-0">
              <label htmlFor="template-search" className="sr-only">
                Search designs or enter a design code
              </label>
              <div className="gradient-border rounded-full flex items-center h-12 pl-4 pr-2 gap-2.5 focus-within:ring-2 focus-within:ring-ink/70 focus-within:ring-offset-2 focus-within:ring-offset-paper transition-shadow">
                <Search className="h-[18px] w-[18px] shrink-0 text-[#6B665C]" aria-hidden="true" />
                <input
                  id="template-search"
                  type="search"
                  placeholder="Search designs or enter a code"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[15px] text-ink placeholder:text-[#6B665C]/80 [&::-webkit-search-cancel-button]:hidden"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-full text-ink/60 hover:text-ink hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/70"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Category chips + sort */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-4 mb-8 sm:mb-10">
          {categories.length > 0 ? (
            <div className="flex gap-2 sm:gap-2.5 flex-wrap" role="group" aria-label="Filter by occasion">
              <button
                type="button"
                onClick={() => handleCategoryChange('')}
                aria-pressed={!selectedCategory}
                className={`${chipBase} ${!selectedCategory ? chipActive : chipInactive}`}
                data-testid="filter-all"
              >
                All
              </button>
              {categories.map((category) => {
                const isActive = selectedCategory === category.id;
                return (
                  <button
                    type="button"
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    aria-pressed={isActive}
                    className={`${chipBase} ${isActive ? chipActive : chipInactive}`}
                    data-testid={`filter-${(category.name || '').toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>
          ) : (
            <div />
          )}
            <div className="flex flex-wrap gap-2 shrink-0 self-start sm:self-auto">
            {brands.length > 0 && (
              <div className="relative">
                <label htmlFor="template-brand" className="sr-only">
                  Filter by designer
                </label>
                <select
                  id="template-brand"
                  value={selectedBrand}
                  onChange={(e) => handleBrandChange(e.target.value)}
                  className={`appearance-none rounded-full h-10 sm:h-11 pl-4 sm:pl-5 pr-10 text-sm sm:text-[15px] font-semibold cursor-pointer ${
                    selectedBrand ? 'bg-ink text-paper border border-ink' : 'gradient-border text-ink'
                  } ${focusRing}`}
                  data-testid="brand-select"
                >
                  <option value="">All designers</option>
                  <option value={AZAMEIN_BRAND}>Azamein</option>
                  <option value={ALL_PARTNERS}>All collections</option>
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className={`pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 ${selectedBrand ? 'text-paper/80' : 'text-ink/70'}`}
                  aria-hidden="true"
                />
              </div>
            )}
            <div className="relative">
              <label htmlFor="template-sort" className="sr-only">
                Sort designs
              </label>
              <select
                id="template-sort"
                value={sort}
                onChange={(e) => handleSortChange(e.target.value)}
                className={`appearance-none gradient-border rounded-full h-10 sm:h-11 pl-4 sm:pl-5 pr-10 text-sm sm:text-[15px] font-semibold text-ink cursor-pointer ${focusRing}`}
                data-testid="sort-select"
              >
                {sortOptions.map((o) => (
                  <option key={o.value || 'featured'} value={o.value}>
                    {o.value ? o.label : 'Sort: Featured'}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/70" aria-hidden="true" />
            </div>
            </div>
          </div>

          {(selectedPartner || selectedBrand === ALL_PARTNERS) && !loading && !error && (
            <PartnerBanner
              brand={selectedPartner}
              brands={brands}
              count={filteredTemplates.length}
              onOpen={handlePartnerOpen}
              onClear={() => handleBrandChange('')}
            />
          )}

          {/* Templates Grid */}
          {loading ? (
            <div
              className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-7 sm:gap-x-6 sm:gap-y-9 lg:gap-x-7 lg:gap-y-10"
              aria-busy="true"
              aria-label="Loading templates"
            >
              {[...Array(8)].map((_, i) => (
                <TemplateCardSkeleton key={i} />
              ))}
            </div>
          ) : error ? (
            <StatePanel role="alert">
              <p className="font-serif text-2xl text-ink mb-2">Something went wrong</p>
              <p className="text-[#4A4640] mb-7">{error}</p>
              <button type="button" className={primaryPill} onClick={() => setReloadKey((k) => k + 1)}>
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Try again
              </button>
            </StatePanel>
          ) : filteredTemplates.length === 0 ? (
            <StatePanel>
              <p className="font-serif text-2xl sm:text-3xl text-ink mb-2">
                No designs <span className="italic font-medium">found</span>
              </p>
              <p className="text-[#4A4640] mb-7">
                {query
                  ? `Nothing matches “${searchQuery.trim()}”. Try a different word or design code.`
                  : selectedBrand
                    ? 'This designer has no templates here yet.'
                    : 'There are no templates in this category yet. Check back soon!'}
              </p>
              <button
                type="button"
                className={secondaryPill}
                onClick={() => {
                  setSearchQuery('');
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('category');
                    next.delete('brand');
                    return next;
                  });
                }}
              >
                Clear filters
              </button>
            </StatePanel>
          ) : (
            <>
              <p className="sr-only" aria-live="polite">
                {filteredTemplates.length} template{filteredTemplates.length === 1 ? '' : 's'} found
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-7 sm:gap-x-6 sm:gap-y-9 lg:gap-x-7 lg:gap-y-10">
                {filteredTemplates.map((template, index) => {
                  const brand = template.partner_id ? brandById.get(template.partner_id) : null;
                  return (
                  <motion.div
                    key={template.id}
                    className="relative min-w-0"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index, 12) * 0.04 }}
                  >
                    {brand && <PartnerTag brand={brand} onOpen={handlePartnerOpen} />}
                    <Link
                      to={`/editor/${template.id}`}
                      className={`group template-card flex flex-col gap-3 sm:gap-4 rounded-2xl ${focusRing}`}
                      data-testid={`template-card-${template.id}`}
                    >
                      <div className="relative gradient-border gb-cream rounded-2xl p-3 sm:p-6 lg:p-7">
                        <div className="rounded-[4px] shadow-[0_18px_30px_-16px_rgba(26,26,26,0.45)] transition-all duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_26px_40px_-18px_rgba(26,26,26,0.5)]">
                          <InvitationPreview
                            backgroundImage={template.thumbnail_image || template.background_image}
                            fields={template.text_fields}
                            perspective={template.perspective}
                            getText={(field) => field.default_text || field.text || ''}
                            aspectRatio="5 / 7"
                            textShadow="0 1px 3px rgba(0,0,0,0.3), 0 1px 1px rgba(255,255,255,0.8)"
                            alt={`${template.name} invitation template`}
                            className="rounded-[4px]"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 sm:gap-3 px-1">
                        <div className="min-w-0 flex flex-col gap-0.5">
                          <h2 className="font-sans text-sm sm:text-base font-bold text-ink truncate">
                            {template.name}
                          </h2>
                          {template.template_code && (
                            <p className="text-xs sm:text-[13px] text-[#6B665C] truncate">
                              Code {template.template_code}
                              {template.background_code ? ` · Background ${template.background_code}` : ''}
                            </p>
                          )}
                          <p className="text-sm sm:text-base font-bold text-ink">
                            {formatPrice(getTemplatePrice(template))}
                          </p>
                        </div>
                        <span
                          className="shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-ink text-paper inline-flex items-center justify-center transition-transform duration-300 group-hover:rotate-45"
                          aria-hidden="true"
                        >
                          <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
